/**
 * Keyword matcher —— 三段式调度。
 *
 * Phase 1 字面 substring：keyword 在 msg/raw 中以子串形式存在 → mode='literal'。
 * Phase 2a 骨架对齐（kw.tokens >= 2）：matchSkeleton + classifyToken 给 diff
 *   打类型 → mode='fuzzy', fuzzyKind='skeleton'。
 * Phase 2b 单 token 启发式（kw.tokens === 1）：detectVariableSegments +
 *   buildFuzzyPattern + .test → mode='fuzzy', fuzzyKind='heuristic'。
 *
 * `rule.strict === true` 时跳过 phase 2，只走字面。
 *
 * 缓存：
 * - `WeakMap<KeywordRule, Tokenized[]>` 每个 keyword 的 tokenize 结果。
 * - `WeakMap<KeywordRule, Map<string, RegExp>>` 单 token 启发式的编译产物。
 */

import {
  classifyToken,
  buildFuzzyPattern,
  detectVariableSegments,
} from '../heuristics';
import { matchSkeleton, tokenize } from '../skeleton';
import type {
  DiffKind,
  KeywordRule,
  LogcatLineView,
} from '../types';
import type { Tokenized } from '../skeleton';

export interface KeywordMatch {
  hit: boolean;
  /** 命中模式；hit=false 时该字段省略。 */
  mode?: 'literal' | 'fuzzy';
  /** mode='fuzzy' 时进一步区分骨架对齐 vs 单 token 启发式。 */
  fuzzyKind?: 'skeleton' | 'heuristic';
  /** 命中的关键字字面量。 */
  matchedKeyword?: string;
  // skeleton
  fuzzyLineTokens?: string[];
  fuzzyWindowStart?: number;
  fuzzyDiffs?: number[];
  fuzzyDiffKinds?: DiffKind[];
  // heuristic
  fuzzyPattern?: string;
}

const KW_TOKENS_CACHE = new WeakMap<KeywordRule, Tokenized[]>();
const KW_REGEX_CACHE = new WeakMap<KeywordRule, Map<string, RegExp | null>>();

interface KeywordsOpts {
  ignoreCase?: boolean;
  strict?: boolean;
}

/**
 * 公共底层：给一组 keywords 跑 phase 1 + phase 2。
 *
 * 与 KeywordRule / SequenceStep 解耦，走 caller 提供的 `cacheKey`（一般就是
 * 规则对象本身）来缓存 tokenize / regex 编译产物。
 */
export function matchKeywordsAgainstLine(
  keywords: ReadonlyArray<string>,
  opts: KeywordsOpts,
  line: LogcatLineView,
  cacheKey?: object,
): KeywordMatch {
  if (!keywords || keywords.length === 0) return { hit: false };

  const ignoreCase = !!opts.ignoreCase;
  const target = ignoreCase ? line.msg.toLowerCase() : line.msg;
  const targetRaw = ignoreCase ? line.raw.toLowerCase() : line.raw;

  // Phase 1 字面 substring
  for (const kRaw of keywords) {
    const k = ignoreCase ? kRaw.toLowerCase() : kRaw;
    if (k.length === 0) continue;
    if (target.includes(k)) {
      return { hit: true, mode: 'literal', matchedKeyword: kRaw };
    }
    if (target !== targetRaw && targetRaw.includes(k)) {
      return { hit: true, mode: 'literal', matchedKeyword: kRaw };
    }
  }

  if (opts.strict) return { hit: false };

  // Phase 2 准备：选 target、tokenize 一次
  const fuzzyTarget = line.msg !== '' ? line.msg : line.raw;
  if (fuzzyTarget === '') return { hit: false };
  const lnT = tokenize(fuzzyTarget);

  // 取/建 keyword tokenize 缓存
  const tokensCache = (() => {
    if (!cacheKey) return null;
    let arr = KW_TOKENS_CACHE.get(cacheKey as KeywordRule);
    if (!arr) {
      arr = keywords.map((k) => tokenize(k));
      KW_TOKENS_CACHE.set(cacheKey as KeywordRule, arr);
    } else if (arr.length !== keywords.length) {
      // keywords 数组改了 → 重算
      arr = keywords.map((k) => tokenize(k));
      KW_TOKENS_CACHE.set(cacheKey as KeywordRule, arr);
    }
    return arr;
  })();

  for (let i = 0; i < keywords.length; i++) {
    const kw = keywords[i];
    if (kw.length === 0) continue;
    const kwT = tokensCache?.[i] ?? tokenize(kw);
    const N = kwT.tokens.length;

    if (N >= 2) {
      // Phase 2a 骨架对齐
      const skel = matchSkeleton(kw, fuzzyTarget, ignoreCase);
      if (skel.hit) {
        const start = skel.windowStart!;
        const diffs = skel.diffs!;
        const diffKinds: DiffKind[] = diffs.map((idx) =>
          classifyToken(lnT.tokens[start + idx]),
        );
        return {
          hit: true,
          mode: 'fuzzy',
          fuzzyKind: 'skeleton',
          matchedKeyword: kw,
          fuzzyLineTokens: lnT.tokens,
          fuzzyWindowStart: start,
          fuzzyDiffs: diffs,
          fuzzyDiffKinds: diffKinds,
        };
      }
    } else if (N === 1) {
      // Phase 2b 单 token 启发式
      const segs = detectVariableSegments(kw);
      if (segs.length === 0) continue;
      // 单段-全文短路：keyword 整体只是一个变量段（如 "12345" / "com.foo.bar.qux"）
      if (
        segs.length === 1 &&
        segs[0].text === kw.trim()
      ) {
        continue;
      }
      const pattern = buildFuzzyPattern(kw, segs);
      const re = getOrCompileRegex(cacheKey, pattern, ignoreCase);
      if (!re) continue;
      try {
        if (re.test(fuzzyTarget)) {
          return {
            hit: true,
            mode: 'fuzzy',
            fuzzyKind: 'heuristic',
            matchedKeyword: kw,
            fuzzyPattern: pattern,
          };
        }
      } catch {
        // 防御：理论上不会抛
      }
    }
  }

  return { hit: false };
}

function getOrCompileRegex(
  cacheKey: object | undefined,
  pattern: string,
  ignoreCase: boolean,
): RegExp | null {
  const flags = ignoreCase ? 'i' : '';
  const cacheKeyStr = `${flags}\u0000${pattern}`;
  if (cacheKey) {
    let bag = KW_REGEX_CACHE.get(cacheKey as KeywordRule);
    if (!bag) {
      bag = new Map();
      KW_REGEX_CACHE.set(cacheKey as KeywordRule, bag);
    }
    if (bag.has(cacheKeyStr)) return bag.get(cacheKeyStr) ?? null;
    let re: RegExp | null = null;
    try {
      re = new RegExp(pattern, flags);
    } catch {
      re = null;
    }
    bag.set(cacheKeyStr, re);
    return re;
  }
  try {
    return new RegExp(pattern, flags);
  } catch {
    return null;
  }
}

/**
 * 判定一行日志是否命中 keyword 规则。
 *
 * tag/level 白名单先过（与原版语义一致）；之后调 `matchKeywordsAgainstLine`
 * 跑三段式。
 */
export function matchKeyword(
  rule: KeywordRule,
  line: LogcatLineView,
): KeywordMatch {
  if (rule.tags && rule.tags.length > 0 && !rule.tags.includes(line.tag)) {
    return { hit: false };
  }
  if (rule.levels && rule.levels.length > 0) {
    const lv = line.level;
    if (lv === '?' || !rule.levels.includes(lv)) return { hit: false };
  }
  return matchKeywordsAgainstLine(
    rule.keywords ?? [],
    { ignoreCase: rule.ignoreCase, strict: rule.strict },
    line,
    rule,
  );
}
